import {
  Accordion as MuiAccordion,
  AccordionDetails as MuiAccordionDetails,
  AccordionSummary as MuiAccordionSummary,
  Box,
  styled,
  FormControlLabel,
  Switch,
  SxProps,
  Theme,
} from '@mui/material';
import {
  DataGrid,
  DataGridProps,
  GridValidRowModel,
  useGridApiRef,
  type GridRenderCellParams,
  type GridSlotProps,
} from '@mui/x-data-grid';
import { SettingsIcon } from 'lucide-react';
import {
  ChangeEvent, useCallback, useMemo, useState,
  type JSX,
  type ReactNode,
} from 'react';
import { APP_HEIGHT } from '@/constants/layout';
import CustomButton from '../Button';
import CustomTypography from '../Typography';
import { corePalette } from '@/themes/colours';
import Pagination from './Pagination';
import LoadingAnimation from '@/components/Animations/LoadingAnimation';

const Wrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  width: '100%',
  height: '100%',
  gap: '8px',
});

const Accordion = styled(MuiAccordion)(({ theme }) => ({
  width: '100%',
  borderBottom: 'none',
  '& h3': {
    ...theme.typography.h3,
    width: '100%',
  },
  '& .MuiCollapse-root': {
    width: '100%',
  },
}));

const AccordionSummary = styled(MuiAccordionSummary)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  padding: '4px',
  minHeight: '0px',
  cursor: 'default !important',
});

const AccordionDetails = styled(MuiAccordionDetails)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  width: '100%',
  padding: '4px',
});

const ColumnToggles = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  flexWrap: 'wrap',
  width: '100%',
  padding: '0px',
  gap: '16px',
});

export type TableConfig = {
  key: string;
  columnLabel: ReactNode;
  isVisible: boolean;
  initialWidth?: number;
  customRender?: (params: GridRenderCellParams) => JSX.Element;
}

interface IProps extends Omit<DataGridProps, 'pagination'> {
  pagination?: boolean;
  tableConfig?: TableConfig[];
  onUpdateConfig?: (config: TableConfig[]) => void;
  onResetConfig?: () => void;
  countLabel?: string;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  hideCountSummary?: boolean;
  getCellSx?: (row: GridValidRowModel, field?: string) => SxProps<Theme>;
  cellsPadding?: string;
  pinScrollSupport?: boolean;
}

function getCellParamsWithoutStyle(
  slotParams: GridSlotProps['cell'],
): Omit<GridSlotProps['cell'], 'style'> {
  const rest = { ...slotParams };
  delete rest.style;
  return rest;
}

export default function CustomDataGrid({
  tableConfig,
  onUpdateConfig,
  onResetConfig,
  countLabel = 'items',
  hideCountSummary = false,
  getCellSx,
  cellsPadding = '16px 8px',
  pinScrollSupport = false,
  pagination = false,
  slots: customSlots,
  apiRef: externalApiRef,
  ...props
}: IProps): JSX.Element {
  const [open, setOpen] = useState<boolean>(false);
  const page = props.paginationModel?.page ?? 0;
  const rowCount = props.rowCount ?? 0;
  const pageSize = pagination && props.paginationModel?.pageSize
    ? props.paginationModel?.pageSize
    : rowCount;
  const internalAPIRef = useGridApiRef();
  const apiRef = externalApiRef ?? internalAPIRef;

  const from = useMemo(() => (
    page * pageSize + 1
  ), [page, pageSize]);
  const to = useMemo(() => Math.min(
    (page + 1) * pageSize,
    rowCount,
  ), [page, pageSize, rowCount]);

  const handleToggleColumn = (
    config: TableConfig[],
    updateConfig: (updatedConfig: TableConfig[]) => void,
    e: ChangeEvent<HTMLInputElement>,
    key: string,
  ): void => {
    const newConfig = config.map((col) => (
      col.key === key ? { ...col, isVisible: e.target.checked } : col
    ));

    updateConfig(newConfig);
  };

  const cell = useCallback<React.JSXElementConstructor<GridSlotProps['cell']>>((params) => {
    const cellParams = pinScrollSupport ? getCellParamsWithoutStyle(params) : params;

    return (
      <Box
        {...cellParams}
        {...(pinScrollSupport ? { 'data-field': params.column.field } : {})}
        padding={cellsPadding}
        display="flex"
        alignItems="center"
        justifyContent="flex-start"
        sx={{
          backgroundColor: corePalette.white,
          borderBottom: `1px solid ${corePalette.grey50}`,
          ...getCellSx?.(params.row, params.column.field),
        }}
      >
        {params.column.renderCell
          ? params.column.renderCell(
            apiRef.current.getCellParams(params.rowId, params.column.field),
          )
          : (
            <CustomTypography truncate width="100%" fontWeight="medium">
              {apiRef.current.getCellValue(params.rowId, params.column.field)}
            </CustomTypography>
          )}
      </Box>
    );
  }, [apiRef, getCellSx, cellsPadding, pinScrollSupport]);

  const loadingOverlay = useCallback((): JSX.Element => (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      height={APP_HEIGHT}
      width="100%"
      bgcolor={corePalette.white}
    >
      <LoadingAnimation />
    </Box>
  ), []);

  return (
    <Wrapper>
      {(!hideCountSummary || (tableConfig && onUpdateConfig)) && (
        <Accordion
          expanded={open}
          sx={{ width: '100%', borderBottom: 'none', padding: 0 }}
          onChange={(e): void => e.stopPropagation()}
        >
          <AccordionSummary>
            {!hideCountSummary && (
              <CustomTypography variant="bodySmall" fontWeight="medium">
                {pagination ? `${from} - ${to} of ${rowCount}` : rowCount}
                &nbsp;
                {countLabel}
              </CustomTypography>
            )}
            {tableConfig && onUpdateConfig && (
              <CustomButton
                variant="subtle"
                label="Manage"
                size="small"
                onClick={(): void => setOpen((prev) => !prev)}
                startIcon={(
                  <SettingsIcon
                    style={{
                      transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'all 0.5s cubic-bezier(.19, 1, .22, 1)',
                    }}
                  />
                )}
                sx={{
                  marginLeft: 'auto',
                }}
              />
            )}
          </AccordionSummary>
          {tableConfig && onUpdateConfig && (
            <AccordionDetails>
              <ColumnToggles>
                {tableConfig.map((col, idx) => (
                  <FormControlLabel
                    key={col.key}
                    label={col.columnLabel}
                    control={(
                      <Switch
                        checked={col.isVisible}
                        onChange={(e): void => (
                          handleToggleColumn(tableConfig, onUpdateConfig, e, col.key)
                        )}
                        disabled={idx === 0}
                      />
                      )}
                  />
                ))}
              </ColumnToggles>
              {onResetConfig && (
                <Box
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="flex-end"
                  marginTop="12px"
                  gap="8px"
                  width="100%"
                >
                  <CustomButton
                    variant="subtle"
                    label="Reset to default"
                    size="small"
                    onClick={onResetConfig}
                  />
                </Box>
              )}
            </AccordionDetails>
          )}
        </Accordion>
      )}
      <DataGrid
        {...props}
        {...(pagination !== false ? { pagination: true as const } : {})}
        loading={props.loading || false}
        apiRef={apiRef}
        columnHeaderHeight={props.columnHeaderHeight ?? 48}
        disableColumnSelector={props.disableColumnSelector}
        disableRowSelectionOnClick={props.disableRowSelectionOnClick ?? true}
        getRowHeight={props.getRowHeight ?? (() => 'auto')}
        paginationMode={props.paginationMode ?? 'server'}
        sx={{
          width: '100%',
          // Removes background color for hovering
          '& .MuiDataGrid-row:hover': {
            backgroundColor: corePalette.white,
          },
          // Removes outline on custom cell content when focused
          '.MuiDataGrid-cell:focus-within': {
            outline: 'none !important',
          },

          // eslint-disable-next-line @typescript-eslint/naming-convention
          '& .MuiDataGrid-columnHeaderTitle': (theme) => ({
            ...theme.typography.label,
          }),

          '& *': {
            borderColor: corePalette.grey50,
          },
          ...props.sx,
        }}
        slots={{
          cell,
          footer: () => null,
          loadingOverlay,
          ...customSlots,
        }}
      />
      {pagination && (
        <Pagination
          rowsPerPage={props.paginationModel?.pageSize ?? 20}
          onRowsPerPageChange={(value: number): void => {
            props.onPageSizeChange?.(value);
          }}
          currentPage={props.paginationModel?.page ?? 0}
          onCurrentPageChange={(value: number): void => {
            props.onPageChange?.(value);
          }}
          totalResults={props.rowCount ?? 0}
        />
      )}
    </Wrapper>
  );
}
